import React, { useState, useEffect } from 'react';
import useIsMobile from '../../hooks/useIsMobile';
import { useDispatch, useSelector } from 'react-redux';
import {
    Button,
    Modal,
    Form,
    Input,
    Select,
    Space,
    message,
    Tooltip,
    Row,
    Tag
} from 'antd';
import {
    GlobalOutlined,
    LockOutlined,
    PlusCircleOutlined,
    UploadOutlined
} from '@ant-design/icons';

import deckIcon from '../../assets/icon/deck.svg';
import languageIcon from '../../assets/icon/language.svg';
import translateIcon from '../../assets/icon/translate.svg';
import shieldQuestionIcon from '../../assets/icon/shield_question.svg';

import './DeckPage.css';
import deckClient from "../../client/DeckClient";
import { setDeck } from '../../redux/actions/deckActions';
import DeckList from "../../components/DeckList/DeckList";

// Импорт изображений флагов
import englishGbFlag from '../../assets/flag/rounded/ENGLISH_GB.png';
import germanFlag from '../../assets/flag/rounded/GERMAN.png';
import spanishEsFlag from '../../assets/flag/rounded/SPANISH_ES.png';
import russianFlag from '../../assets/flag/rounded/RUSSIAN.png';
import chineseSimplifiedFlag from '../../assets/flag/rounded/CHINESE_SIMPLIFIED.png';
import italianFlag from '../../assets/flag/rounded/ITALIAN.png';
import polishFlag from '../../assets/flag/rounded/POLISH.png';
import frenchFlag from '../../assets/flag/rounded/FRENCH.png';
import englishUsFlag from '../../assets/flag/rounded/ENGLISH_US.png';
import arabicFlag from '../../assets/flag/rounded/ARABIC.png';
import portuguesePtFlag from '../../assets/flag/rounded/PORTUGUESE_PT.png';

const { Option } = Select;
const initialDeckForm = {
    title: '',
    description: '',
    type: '',
    visibility: '',
    locale: '',
    translationLanguage: ''
};

const deckTypes = ['CARD', 'QUIZ', 'WORD'];
const deckVisibilityOptions = ['PRIVATE', 'PUBLIC'];

// Объекты с данными о языках, включая флаги и коды
const locale = [
    { name: 'English (British)', code: 'ENGLISH_GB', flag: englishGbFlag },
    { name: 'Spanish (Spain)', code: 'SPANISH_ES', flag: spanishEsFlag },
    { name: 'German', code: 'GERMAN', flag: germanFlag },
    { name: 'French (France)', code: 'FRENCH_FR', flag: frenchFlag },
    { name: 'Italian', code: 'ITALIAN', flag: italianFlag },
    { name: 'Chinese (Simplified)', code: 'CHINESE_SIMPLIFIED', flag: chineseSimplifiedFlag },
    { name: 'Polish', code: 'POLISH', flag: polishFlag },

];

const translateLocale = [
    { name: 'English (British)', code: 'ENGLISH_GB', flag: englishGbFlag },
    { name: 'Spanish (Spain)', code: 'SPANISH_ES', flag: spanishEsFlag },
    { name: 'German', code: 'GERMAN', flag: germanFlag },
    { name: 'French (France)', code: 'FRENCH_FR', flag: frenchFlag },
    { name: 'Italian', code: 'ITALIAN', flag: italianFlag },
    { name: 'Russian', code: 'RUSSIAN', flag: russianFlag },
    { name: 'Polish', code: 'POLISH', flag: polishFlag },


];

// Выбрать при создании можно семь языков, а колода может быть на любом из
// двадцати девяти, которые знает бэкенд: колоду мог завести другой клиент или
// прежняя версия списка. Для показа берётся эта таблица — всё, на что у нас
// есть флаг, — а неизвестный код показывается как есть, а не теряется.
const localeDisplay = [
    { name: 'English (British)', code: 'ENGLISH_GB', flag: englishGbFlag },
    { name: 'English (American)', code: 'ENGLISH_US', flag: englishUsFlag },
    { name: 'Spanish (Spain)', code: 'SPANISH_ES', flag: spanishEsFlag },
    { name: 'German', code: 'GERMAN', flag: germanFlag },
    { name: 'French (France)', code: 'FRENCH_FR', flag: frenchFlag },
    { name: 'Italian', code: 'ITALIAN', flag: italianFlag },
    { name: 'Russian', code: 'RUSSIAN', flag: russianFlag },
    { name: 'Polish', code: 'POLISH', flag: polishFlag },
    { name: 'Chinese (Simplified)', code: 'CHINESE_SIMPLIFIED', flag: chineseSimplifiedFlag },
    { name: 'Arabic', code: 'ARABIC', flag: arabicFlag },
    { name: 'Portuguese (Portugal)', code: 'PORTUGUESE_PT', flag: portuguesePtFlag },
];

const describeLocale = (code) => {
    if (!code) {
        return null;
    }
    const known = localeDisplay.find((option) => option.code === code);
    return known ? { ...known } : { name: code, code, flag: null };
};



const DeckPage = () => {
    const [decks, setDecks] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [currentDeck, setCurrentDeck] = useState(initialDeckForm);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedDeckId, setSelectedDeckId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [form] = Form.useForm();

    const dispatch = useDispatch();
    const selectedDeck = useSelector((state) => state.deck.selectedDeck);

    useEffect(() => {
        fetchDecks(); // Загружаем списки колод при монтировании компонента
        // fetchDecks намеренно не в зависимостях: он читает selectedDeck, чтобы восстановить
        // активную колоду, и включение его сюда перезапрашивало бы весь список при каждом
        // выборе колоды. Эффект означает ровно «один раз при монтировании».
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);






    const fetchDecks = (newDeckId = null) => {
        setLoading(true);
        deckClient.getAllDecks()
            .then((response) => {
                const updatedDecks = response.map((deck) => ({
                    ...deck,
                    locale: describeLocale(deck.locale),
                    translateLocale: describeLocale(deck.translateLocale),
                }));

                setDecks(updatedDecks);
                setLoading(false);

                // Устанавливаем активной новую Deck, если передан её ID
                if (newDeckId) {
                    const newDeck = updatedDecks.find(deck => deck.deckId === newDeckId);
                    if (newDeck) {
                        dispatch(setDeck(newDeck));
                        setSelectedDeckId(newDeckId);
                    }
                } else if (selectedDeck) {
                    // Если ID не передан, восстанавливаем текущую активную Deck
                    const selectedDeckObj = updatedDecks.find(deck => deck.deckId === selectedDeck.deckId);
                    if (selectedDeckObj) {
                        setCurrentDeck(selectedDeckObj);
                        setSelectedDeckId(selectedDeckObj.deckId);
                    }
                }
            })
            .catch((error) => {
                console.error('Error fetching decks:', error);
                message.error('Failed to load decks.');
                setLoading(false);
            });
    };








    const handleChange = (_, allValues) => {
        setCurrentDeck(allValues); // Все значения формы обновляются в состоянии
    };


    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setOpenDialog(false);
            const saveOrUpdate = isEditMode
                ? deckClient.updateDeck(values.deckId, values)
                : deckClient.createDeck(values);
            saveOrUpdate
                .then((response) => {
                    setOpenDialog(false);
                    form.resetFields();
                    setIsEditMode(false);

                    fetchDecks(response.deckId);
                    message.success(isEditMode ? 'Deck successfully updated!' : 'Deck successfully created!');
                })
                .catch((error) => {
                    console.error('Error saving deck:', error);
                    message.error('Failed to save deck.');
                });
        } catch (validationError) {
            console.log('Validation failed:', validationError);
            message.error('Please fill in all required fields!');
        }
    };




    const handleCreateNew = () => {
        setCurrentDeck({
            title: undefined,
            description: undefined,
            type: undefined,
            visibility: undefined,
            locale: undefined,
            translateLocale: undefined,
        });
        form.setFieldsValue({
            title: undefined,
            description: undefined,
            type: undefined,
            visibility: undefined,
            locale: undefined,
            translateLocale: undefined,
        });
        setIsEditMode(false);
        setOpenDialog(true);
    };




    const handleEdit = (deck) => {
        const editedDeck = {
            ...deck,
            locale: deck.locale?.code || undefined, // Извлекаем код языка
            translateLocale: deck.translateLocale?.code || undefined, // Извлекаем код перевода
        };
        setCurrentDeck(editedDeck); // Сохраняем преобразованные данные в состоянии
        form.setFieldsValue(editedDeck); // Устанавливаем значения формы
        setIsEditMode(true);
        setOpenDialog(true);
    };



    const handleDelete = (deckId) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this deck?',
            content: 'This action cannot be undone.',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            okButtonProps: {
                style: {
                    backgroundColor: "#fff",
                    borderColor: "#8B0000",
                    color: "#8B0000",
                },
            },
            cancelButtonProps: {
                style: {
                    backgroundColor: "#fff",
                    borderColor: "#027b83",
                    color: "#027b83",
                },
            },

            onOk() {
                setOpenDialog(false);
                deckClient.deleteDeck(deckId).then(() => {
                    fetchDecks();
                    if (selectedDeck && selectedDeck.deckId === deckId) {
                        dispatch(setDeck(null));
                        setSelectedDeckId(null);
                    }
                   // Закрыть модальное окно после успешного удаления
                    message.success('Deck successfully deleted!');


                }).catch(error => {
                    console.error(error);
                    message.error('Failed to delete deck.');
                });
            },
            onCancel() {
                console.log('Deletion cancelled');
            },
        });
    };

    const handleSetup = (deck) => {
        console.log('Setup clicked for deck:', deck);
    };

    const renderVisibility = (visibility) => {
        if (visibility === 'PRIVATE') {
            return (
                <Tooltip title="Private">
                    <Space>
                        <LockOutlined />
                        <span>Private</span>
                    </Space>
                </Tooltip>
            );
        } else if (visibility === 'PUBLIC') {
            return (
                <Tooltip title="Public">
                    <Space>
                        <GlobalOutlined />
                        <span>Public</span>
                    </Space>
                </Tooltip>
            );
        } else {
            return null;
        }
    };

    const handleRowClick = (deck) => {
        setSelectedDeckId(deck.deckId);
        dispatch(setDeck(deck));
    };










    const isMobile = useIsMobile();

    return (
        <div style={{ padding: '6px' }}>
            <Row justify="end" style={{ marginBottom: 16 }}>
                <Space>
                    <Button icon={<UploadOutlined />}>Import CSV</Button>
                    <Button type="primary" icon={<PlusCircleOutlined />} onClick={handleCreateNew}>
                        Add Deck
                    </Button>
                </Space>
            </Row>
            <DeckList
                decks={decks}
                loading={loading}
                selectedDeckId={selectedDeckId}
                isMobile={isMobile}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onSetup={handleSetup}
                onRowClick={handleRowClick}
                renderVisibility={renderVisibility}
                localeOptions={localeDisplay}
                translateLocaleOptions={localeDisplay}
            />




            <Modal
                title={isEditMode ? 'Edit Deck' : 'Create New Deck'}
                open={openDialog}
                onCancel={() => {
                    setOpenDialog(false);
                    form.resetFields(); // Сбросить поля формы после закрытия
                }}
                footer={[
                    isEditMode && (
                        <Button
                            key="delete"
                            danger
                            onClick={() => handleDelete(currentDeck.deckId)}
                            style={{ float: 'left' }}
                        >
                            Delete
                        </Button>
                    ),
                    <Button
                        key="cancel"
                        onClick={() => {
                            setOpenDialog(false);
                            form.resetFields();
                        }}
                    >
                        Cancel
                    </Button>,
                    <Button key="save" type="primary" onClick={handleSave}>
                        Save
                    </Button>,
                ]}
                centered
            >
                <Form
                    form={form}
                    layout="vertical"
                    onValuesChange={handleChange}
                >




                    {/* Скрытое поле для deckId */}
                    <Form.Item name="deckId" style={{ display: 'none' }}>
                        <Input type="hidden" />
                    </Form.Item>

                    <Form.Item
                        name="title"
                        rules={[{ required: true, message: 'Please enter a deck title!' }]}
                    >
                        <Input placeholder="Enter deck title" />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        rules={[{ required: true, message: 'Please enter a description!' }]}
                    >
                        <Input.TextArea rows={4} placeholder="Enter deck description" />
                    </Form.Item>

                    {/* Поле Type */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '16px' }}>
                        <img
                            src={deckIcon}
                            alt="Deck Icon"
                            style={{ width: '24px', height: '24px', marginRight: '8px', marginTop: '3px' }}
                        />
                        <Form.Item
                            name="type"
                            style={{ flex: 1, marginBottom: 0 }}
                            rules={[{ required: true, message: 'Please select a deck type!' }]}
                        >
                            <Select placeholder="Select deck type" disabled={isEditMode}>
                                {deckTypes.map((type) => (
                                    <Option key={type} value={type}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span>{type}</span>
                                            {type === 'WORD' && (
                                                <Tag color="orange" style={{ marginLeft: 'auto' }}>
                                                    Beta
                                                </Tag>
                                            )}
                                        </div>
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>

                    {/* Поле Learning Language */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '16px' }}>
                        <img
                            src={languageIcon}
                            alt="Language Icon"
                            style={{ width: '24px', height: '24px', marginRight: '8px', marginTop: '3px' }}
                        />
                        <Form.Item
                            name="locale"
                            style={{ flex: 1, marginBottom: 0 }}
                            rules={[{ required: true, message: 'Please select a learning language!' }]}
                        >
                            <Select placeholder="Select learning language" allowClear disabled={isEditMode}>
                                {locale.map(({ name, code, flag }) => (
                                    <Option key={code} value={code}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <img
                                                src={flag}
                                                alt={name}
                                                style={{ width: '20px', height: '15px', marginRight: '8px' }}
                                            />
                                            <span>{name}</span>
                                        </div>
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>

                    {/* Поле Translation Language */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '16px' }}>
                        <img
                            src={translateIcon}
                            alt="Translation Icon"
                            style={{ width: '24px', height: '24px', marginRight: '8px', marginTop: '3px' }}
                        />
                        <Form.Item
                            name="translateLocale"
                            style={{ flex: 1, marginBottom: 0 }}
                            rules={[{ required: true, message: 'Please select a translation language!' }]}
                        >
                            <Select placeholder="Select translation language" allowClear disabled={isEditMode}>
                                {translateLocale.map(({ name, code, flag }) => (
                                    <Option key={code} value={code}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <img
                                                src={flag}
                                                alt={name}
                                                style={{ width: '20px', height: '15px', marginRight: '8px' }}
                                            />
                                            <span>{name}</span>
                                        </div>
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>

                    {/* Поле Visibility */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '16px' }}>
                        <img
                            src={shieldQuestionIcon}
                            alt="Visibility Icon"
                            style={{ width: '24px', height: '24px', marginRight: '8px', marginTop: '3px' }}
                        />
                        <Form.Item
                            name="visibility"
                            style={{ flex: 1, marginBottom: 0 }}
                            rules={[{ required: true, message: 'Please select visibility!' }]}
                        >
                            <Select placeholder="Select visibility" allowClear>
                                {deckVisibilityOptions.map((option) => (
                                    <Option key={option} value={option}>
                                        {option === 'PRIVATE' ? (
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <LockOutlined style={{ marginRight: '8px' }} />
                                                <span>Private</span>
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <GlobalOutlined style={{ marginRight: '8px' }} />
                                                <span>Public</span>
                                            </div>
                                        )}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>
                </Form>
            </Modal>


        </div>
    );
};

export default DeckPage;
